import "./OrderStepComponent.scss";
import template from "./OrderStepComponent.html";
import {PlaceOrderWizardComponentController} from "../../../PlaceOrderWizardComponent";
import {TradefloorApiService} from "../../../../../../../js/services/tradefloorApi.service";
import {IOnDestroy, IOnInit} from "angular";
import {FeatureNames} from "../../../../../../../types/UserConfig";
import {BaseStrategy} from "../../../../types/WhatIf";
import {OptionsTicket, OptionsWizardOrder} from "../../../../types/OptionsTicket";
import {OrderSpreads} from "../../../models/OrderSpreads";
import {distinct, flatMap, isNotNull} from "../../../../../../../js/util/UtilFunctions";
import {OrderTicketUtil} from "../../../../util/OrderTicketUtil";
import {ChainService, Option, OptionChain} from "../../../../../../../types/ChainService";
import {StringKeyed} from "../../../../../../../types/GenericTypes";
import {SecurityUtil} from "../../../../../../../js/util/SecurityUtil";
import {PortfolioType} from "../../../../../../models/Portfolio";
import {ArrayUtil} from "../../../../../../../js/util/ArrayUtil";
import {ObjectUtil} from "../../../../../../../js/util/ObjectUtil";
import {PriceStrategyService} from "../../../../../../../components/inputs/price/service/PriceStrategyService";
import {CurrentUserService} from "../../../../../../../services/user/CurrentUserService";
import {Observable, ReplaySubject, Scheduler} from "rxjs";

export const OrderStep = "orderStep";

export function orderStep() {
    return {
        template,
        require: {
            wizard: "^^placeOrderWizard"
        },
        bindings: {
            whatIf: "<",
            orders: "<",
            portfolioType: "<",
            onChange: "&"
        },
        controllerAs: "ctrl",
        controller: OrderStepComponentController
    };
}

const PriceUpdateIntervalSeconds = 5;
const PriceUpdateInterval = PriceUpdateIntervalSeconds * 1000;

export class OrderStepComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public set whatIf(base: BaseStrategy | null) {
        if (base != null) {
            this.whatIf$.next(base);
        }
    }

    private readonly whatIf$ = new ReplaySubject<BaseStrategy>(1);

    /** @input */
    public set orders(orders: ReadonlyArray<OptionsWizardOrder> | null) {
        this.orders$.next(orders);
    }

    private readonly orders$ = new ReplaySubject<ReadonlyArray<OptionsWizardOrder> | null>(1);

    /** @input */
    public set portfolioType(type: PortfolioType | null) {
        if (type != null) {
            this.portfolioType$.next(type);
        }
    }

    private readonly portfolioType$ = new ReplaySubject<PortfolioType>(1);

    public readonly onChange: Function;

    public readonly wizard: PlaceOrderWizardComponentController;

    private readonly tickets$ = new ReplaySubject<ReadonlyArray<OptionsTicket>>(1);

    private _chain: Promise<OptionChain> | null;

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly tradefloorApi: TradefloorApiService,
                       private readonly currentUserService: CurrentUserService,
                       private readonly chainService: ChainService,
                       private readonly priceStrategyService: PriceStrategyService) {
    }

    public async $onInit() {
        this.updatePrices();
        this.watchWhatIf();
        this.watchOrderSources();
        this.watchOrdersChanged();
    }

    private watchWhatIf() {
        this.whatIf$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((whatIf) => whatIf.trades)
            .withLatestFrom(this.orders$)
            .map(([trades, orders]) => this.buildTickets(trades, orders))
            .subscribe((tickets) => this.tickets$.next(tickets));
    }

    private buildTickets(trades: ReadonlyArray<BaseTrade>,
                         orders: ReadonlyArray<OptionsWizardOrder> | null): ReadonlyArray<OptionsTicket> {
        return trades.map((tr) => {
            const ticket = OrderTicketUtil.buildTicket(tr);
            if (orders != null) {
                const previousOrder = orders.find((o) => o.ticket.code === tr.code);
                if (previousOrder != null) {
                    ticket.limit = previousOrder.ticket.limit;
                    ticket.quantity = previousOrder.ticket.quantity;
                }
            }
            return ticket;
        });
    }

    private watchOrderSources() {
        const source$ = Observable.combineLatest(this.tickets$, this.portfolioType$, this.orders$);
        source$.observeOn(Scheduler.async)
               .takeUntil(this.destroyed$)
               .filter(([tickets, portfolioType, orders]) => orders == null)
               .flatMap(([tickets, portfolioType]) => this.createOrders(tickets, portfolioType))
               .subscribe((orders) => this.orders$.next(orders));
    }

    private async createOrders(tickets: ReadonlyArray<OptionsTicket>,
                               portfolioType: PortfolioType): Promise<ReadonlyArray<OptionsWizardOrder>> {
        const trades = tickets.map((t) => ObjectUtil.pick(t, "code", "quantity"));
        const spreads = await this.retrieveSpreads(trades, portfolioType);

        const ticketsByUnderlying = ArrayUtil.groupBy(tickets, (trade) => SecurityUtil.underlying(trade.code));
        const underlyingCodes = ObjectUtil.keys(ticketsByUnderlying);

        const orderList = underlyingCodes.map(async (underlyingCode) => {
            const ticketList = ticketsByUnderlying[underlyingCode];
            const options = await this.optionsForLegs(ticketList);
            return ticketList.map((ticket) => {
                const spread = spreads.spreadFor(ticket.code)
                                      .orElseThrow(() => Error(`Unable to find spread for ${ticket.code}`));
                const underlying = SecurityUtil.underlying(ticket.code);
                if ((ticket.limit == null) && (spread.mid != null)) {
                    ticket.limit = this.priceStrategyService.forCode(ticket.code).round(spread.mid);
                }
                return {
                    ticket,
                    spread,
                    underlying,
                    side: OrderTicketUtil.side(ticket.quantity),
                    option: options[ticket.code]
                };
            });
        });

        const promiseOrderList = await Promise.all(orderList);
        return promiseOrderList.reduce((acc, next) => flatMap(acc, next), []);
    }

    private async retrieveSpreads(trades: Array<BaseTrade>, portfolioType: PortfolioType): Promise<OrderSpreads> {
        const codes = trades.map(tr => tr.code)
                            .filter(distinct);
        const data = await this.tradefloorApi.retrieveOrderSpread(codes, portfolioType);
        return OrderSpreads.of(data.bidAskSummaryList);
    }

    private async optionsForLegs(trades: Array<BaseTrade>): Promise<StringKeyed<Option>> {
        const underlying = OrderTicketUtil.underlyingFor(trades);
        const chain = await this.chainFor(underlying);
        return trades.map(trade => {
            const option = chain.codes[trade.code];
            return {[trade.code]: option};
        }).reduce((acc, next) => Object.assign(acc, next));
    }

    /* Lazy Load the Chain and then keep a reference for when we need to requery it */
    private async chainFor(underlying: string): Promise<OptionChain> {
        if (this._chain == null) {
            this._chain = this.chainService(underlying);
        }
        return this._chain;
    }

    private watchOrdersChanged() {
        this.orders$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .subscribe((orders) => this.onChange({orders}));
    }

    private updatePrices() {
        if (this.currentUserService.hasFeature(FeatureNames.FEATURE_LIVE_PRICES)) {
            const timer$ = Observable.interval(PriceUpdateInterval);
            const source$ = Observable.combineLatest(this.orders$, this.portfolioType$, timer$);
            source$.takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async)
                   .filter(([orders, portfolioType]) => isNotNull(orders) && isNotNull(portfolioType))
                   .subscribe(([orders, portfolioType]) => this.updateOrderSpread(orders!, portfolioType));
        }
    }

    private async updateOrderSpread(orders: ReadonlyArray<OptionsWizardOrder>, portfolioType: PortfolioType) {
        const trades = orders.map((o) => o.ticket);
        const spreads = await this.retrieveSpreads(trades, portfolioType);
        orders.forEach(ord => {
            ord.spread = spreads.spreadFor(ord.ticket.code)
                                .orElseThrow(() => Error(`Unable to find spread for ${ord.ticket.code}`));
        });
    }

    public removeTrade(trade: OptionsWizardOrder) {
        this.orders$
            .first()
            .timeout(100)
            .filter(isNotNull)
            .map((orders) => orders.filter((o) => o.ticket.code !== trade.ticket.code))
            .do((orders) => {
                if (orders.length === 0) {
                    this.wizard.cancel();
                }
            })
            .subscribe((orders) => this.orders$.next(orders));
    }

    public $onDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
